import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ExcelJS from 'exceljs';
import { machineryService } from '../../../services/machineryService';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import { formatDateLocaleLima, normalizeTime } from '../../../utils/dateUtils';
import { formatBitacoraReading } from '../../../utils/bitacoraFormat';

const documentRefLabel = (ev) => {
  if (ev.document_id && (ev.document_code || ev.document_series || ev.document_number)) {
    return (ev.document_series != null && ev.document_series !== '') || (ev.document_number != null && ev.document_number !== '')
      ? [ev.document_series, ev.document_number].filter(Boolean).join('-')
      : (ev.document_code || '');
  }
  if (ev.service_id && ev.service_sku) return ev.service_sku;
  if (ev.entry_guide_id && ev.entry_guide_code) return ev.entry_guide_code;
  return '';
};

const vinculacionLabel = (ev) => {
  if (ev.document_id) return 'Documento';
  if (ev.service_id) return 'Servicio';
  if (ev.entry_guide_id) return 'Guía';
  return 'Bitácora';
};

const resolveDocumentRoute = (ev) => {
  if (!ev?.document_id) return null;
  const isContinuousDocument = ev.document_type === 'suministro_continuo';
  if (isContinuousDocument) {
    return `/supply-continuous-documents/${ev.document_id}`;
  }
  return `/documents/${ev.document_id}/edit`;
};

const Events = () => {
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [machines, setMachines] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [machineFilter, setMachineFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [shiftIssuesModal, setShiftIssuesModal] = useState({ open: false, event: null });
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    const machineId = searchParams.get('machine_id') || '';
    const vehicleId = searchParams.get('vehicle_id') || '';
    setMachineFilter(machineId);
    setVehicleFilter(vehicleId);
  }, [searchParams]);

  const loadEventsListData = async () => {
    const machineId = searchParams.get('machine_id') || machineFilter || undefined;
    const vehicleId = searchParams.get('vehicle_id') || vehicleFilter || undefined;
    setLoading(true);
    try {
      const res = await machineryService.getEventsListData({
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        machineId: machineId || undefined,
        vehicleId: vehicleId || undefined
      });
      if (res.success && res.data) {
        setEvents(res.data.events || []);
        setMachines(res.data.machines || []);
        setVehicles(res.data.vehicles || []);
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al cargar datos', type: 'error' });
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al cargar datos', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEventsListData();
  }, [dateFrom, dateTo, machineFilter, vehicleFilter, searchParams]);

  const handleDelete = (event) => {
    setEventToDelete(event);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!eventToDelete) return;
    try {
      const response = await machineryService.deleteEvent(eventToDelete.id);
      if (response.success) {
        setToast({ isOpen: true, message: 'Evento eliminado correctamente', type: 'success' });
        loadEventsListData();
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al eliminar', type: 'error' });
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al eliminar el evento', type: 'error' });
    } finally {
      setIsDeleteModalOpen(false);
      setEventToDelete(null);
    }
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setMachineFilter('');
    setVehicleFilter('');
  };

  const currentMachine = machineFilter ? machines.find(m => String(m.id) === String(machineFilter)) : null;
  const currentVehicle = vehicleFilter ? vehicles.find(v => String(v.id) === String(vehicleFilter)) : null;
  const bitacoraDe = currentMachine ? `${currentMachine.code} ${currentMachine.name}` : currentVehicle ? `${currentVehicle.code} ${currentVehicle.name}` : null;
  const isSingleResourceView = !!(machineFilter || vehicleFilter);
  const eventsWithShiftIssues = events.filter((ev) => (ev.shift_inconsistencies || []).length > 0);

  const exportToExcel = () => {
    if (!events.length) {
      setToast({ isOpen: true, message: 'No hay datos para exportar', type: 'warning' });
      return;
    }
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Eventos bitácora', { views: [{ state: 'frozen', ySplit: 2, activeCell: 'A3' }] });
    const borderThin = {
      top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      right: { style: 'thin', color: { argb: 'FFD1D5DB' } },
    };
    const titleStyle = {
      font: { bold: true, size: 14, color: { argb: 'FF1F2937' } },
      alignment: { vertical: 'middle' },
    };
    const subtitleStyle = {
      font: { size: 11, color: { argb: 'FF6B7280' } },
      alignment: { vertical: 'middle', wrapText: true },
    };
    const headerStyle = {
      font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' } },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } },
      alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
      border: borderThin,
    };
    const cellLeft = { alignment: { horizontal: 'left', vertical: 'middle', wrapText: true }, border: borderThin };
    const cellRight = { alignment: { horizontal: 'right', vertical: 'middle' }, border: borderThin };

    const filterParts = [];
    if (dateFrom) filterParts.push(`Desde: ${dateFrom}`);
    if (dateTo) filterParts.push(`Hasta: ${dateTo}`);
    if (bitacoraDe) filterParts.push(`Bitácora: ${bitacoraDe}`);
    const subtitle = filterParts.length ? filterParts.join(' · ') : 'Listado general de eventos';

    const numCols = isSingleResourceView ? 10 : 11;
    ws.addRow(['Eventos de bitácora (Maquinaria)']);
    ws.getRow(1).height = 24;
    ws.getCell(1, 1).style = titleStyle;
    ws.mergeCells(1, 1, 1, numCols);
    ws.addRow([subtitle]);
    ws.getRow(2).height = 28;
    ws.getCell(2, 1).style = subtitleStyle;
    ws.mergeCells(2, 1, 2, numCols);

    let headers;
    let widths;
    if (isSingleResourceView) {
      headers = ['Fecha / hora', 'Documento / ref.', 'Horómetro (h)', 'Kilómetros', 'Tipo de evento', 'Operador', 'Proyecto', 'Notas', 'Revisión jornada', 'Vinculación'];
      widths = [18, 18, 12, 12, 22, 22, 24, 28, 24, 14];
    } else {
      headers = ['Fecha / hora', 'Máquina', 'Vehículo', 'Documento / ref.', 'Horómetro (h)', 'Kilómetros', 'Tipo de evento', 'Operador', 'Proyecto', 'Notas', 'Vinculación'];
      widths = [18, 18, 18, 18, 12, 12, 20, 20, 22, 28, 14];
    }
    const hRow = ws.addRow(headers);
    hRow.height = 22;
    hRow.eachCell((c, colNumber) => {
      c.style = { ...headerStyle };
      ws.getColumn(colNumber).width = widths[colNumber - 1] || 14;
    });

    events.forEach((ev) => {
      const fechaHora = ev.event_date
        ? [formatDateLocaleLima(ev.event_date), ev.event_time ? normalizeTime(ev.event_time) : null].filter(Boolean).join(' ')
        : '—';
      const docRef = documentRefLabel(ev) || '—';
      const horo = ev.hours != null ? formatBitacoraReading(ev.hours) : '—';
      const km = ev.kilometers != null ? formatBitacoraReading(ev.kilometers) : '—';
      const revision = (ev.shift_inconsistencies || []).length > 0 ? ev.shift_inconsistencies.join('; ') : '—';
      const rowVals = isSingleResourceView
        ? [fechaHora, docRef, horo, km, ev.event_type || '—', ev.operator || '—', ev.project_name || '—', ev.notes || '—', revision, vinculacionLabel(ev)]
        : [
            fechaHora,
            ev.machine_code ? `${ev.machine_code} ${ev.machine_name || ''}`.trim() : '—',
            ev.vehicle_code ? `${ev.vehicle_code} ${ev.vehicle_name || ''}`.trim() : '—',
            docRef,
            horo,
            km,
            ev.event_type || '—',
            ev.operator || '—',
            ev.project_name || '—',
            ev.notes || '—',
            vinculacionLabel(ev),
          ];
      const dataRow = ws.addRow(rowVals);
      dataRow.eachCell((cell, colNumber) => {
        const isNumCol = isSingleResourceView ? (colNumber === 3 || colNumber === 4) : (colNumber === 5 || colNumber === 6);
        cell.style = isNumCol ? { ...cellRight } : { ...cellLeft };
      });
    });

    wb.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `Bitacora_eventos_${stamp}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      setToast({ isOpen: true, message: 'Exportado correctamente', type: 'success' });
    }).catch(() => {
      setToast({ isOpen: true, message: 'Error al exportar', type: 'error' });
    });
  };

  return (
    <div className="p-4 sm:p-6 min-w-0 max-w-full">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between min-w-0">
        <div>
          <Link to="/machinery/events" className="text-sm text-teal-600 hover:text-teal-800 mb-1 inline-block">← Volver a Bitacora</Link>
          <h1 className="text-2xl font-bold text-gray-800">Eventos (Bitacora)</h1>
          <p className="text-gray-600 mt-1">
            {bitacoraDe ? <>Bitacora de: <span className="font-medium text-gray-800">{bitacoraDe}</span></> : 'Maquinaria y vehículos con horómetro y/o kilometraje; la tabla muestra ambos'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/machinery/events"
            className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md text-sm font-medium transition-colors"
          >
            Volver
          </Link>
          <button
            type="button"
            onClick={exportToExcel}
            disabled={loading || events.length === 0}
            className="px-4 py-2 border border-teal-600 text-teal-700 hover:bg-teal-50 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Exportar Excel
          </button>
          <Link
            to={machineFilter ? `/machinery/events/create?machine_id=${machineFilter}` : vehicleFilter ? `/machinery/events/create?vehicle_id=${vehicleFilter}` : '/machinery/events/create'}
            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-sm font-medium transition-colors"
          >
            + Nuevo Evento
          </Link>
        </div>
      </div>

      {/* Filtros: solo se muestran cuando se ve la lista general (no desde un vehículo/máquina) */}
      {!isSingleResourceView && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha desde</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha hasta</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Máquina</label>
              <select
                value={machineFilter}
                onChange={(e) => setMachineFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Todas</option>
                {machines.map(m => <option key={m.id} value={m.id}>{m.code} - {m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehículo</label>
              <select
                value={vehicleFilter}
                onChange={(e) => setVehicleFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Todos</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.code} - {v.name}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={clearFilters}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Limpiar
              </button>
            </div>
          </div>
        </div>
      )}

      {isSingleResourceView && eventsWithShiftIssues.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Se detectaron <span className="font-semibold">{eventsWithShiftIssues.length}</span> evento(s) de jornada con inconsistencias
          (secuencia inicio/fin, duplicados por día o desviación de KM). Revise la columna <span className="font-semibold">Rev.</span>
        </div>
      )}

      {/* Tabla */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          <p className="mt-2 text-gray-600">Cargando eventos...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500">No se encontraron eventos</p>
          <Link to={machineFilter ? `/machinery/events/create?machine_id=${machineFilter}` : vehicleFilter ? `/machinery/events/create?vehicle_id=${vehicleFilter}` : '/machinery/events/create'} className="mt-4 inline-block text-teal-600 hover:text-teal-700 font-medium">
            Registrar primer evento
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden w-full min-w-0">
          <div className="w-full min-w-0 overflow-hidden">
            <table className="w-full table-fixed divide-y divide-gray-200 text-xs sm:text-sm">
              <colgroup>
                {isSingleResourceView ? (
                  <>
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '14%' }} />
                  </>
                ) : (
                  <>
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '6%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '19%' }} />
                  </>
                )}
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-700 uppercase tracking-tight">Fecha / Hora</th>
                  {!isSingleResourceView && (
                    <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-700 uppercase tracking-tight">Máquina</th>
                  )}
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-700 uppercase tracking-tight">Doc.</th>
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-700 uppercase tracking-tight">Horóm.</th>
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-700 uppercase tracking-tight">Km</th>
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-700 uppercase tracking-tight">Tipo</th>
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-700 uppercase tracking-tight">Operador</th>
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-700 uppercase tracking-tight">Proyecto</th>
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-700 uppercase tracking-tight">Notas</th>
                  {isSingleResourceView && (
                    <th className="px-2 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-700 uppercase tracking-tight">Rev.</th>
                  )}
                  <th className="px-2 py-2 text-right text-[10px] sm:text-xs font-medium text-gray-700 uppercase tracking-tight">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {events.map((ev) => (
                  <tr key={ev.id} className={`hover:bg-gray-50 ${(ev.shift_inconsistencies || []).length > 0 ? 'bg-amber-50/40' : ''}`}>
                    <td className="px-2 py-2 whitespace-nowrap text-gray-900 tabular-nums">
                      {ev.event_date ? [formatDateLocaleLima(ev.event_date), ev.event_time ? normalizeTime(ev.event_time) : null].filter(Boolean).join(' ') : '-'}
                    </td>
                    {!isSingleResourceView && (
                      <td className="px-2 py-2 min-w-0 align-top">
                        {ev.machine_code ? (
                          <div className="truncate" title={`${ev.machine_code} ${ev.machine_name || ''}`}>
                            <span className="font-medium text-gray-900">{ev.machine_code}</span>
                            <span className="text-gray-500 block truncate text-[11px]">{ev.machine_name}</span>
                          </div>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                    )}
                    <td className="px-2 py-2 whitespace-nowrap align-top">
                      {ev.document_id && (ev.document_code || ev.document_series || ev.document_number) ? (
                        <Link to={resolveDocumentRoute(ev)} className="text-teal-600 hover:text-teal-800 font-medium truncate block" title={(ev.document_series != null && ev.document_series !== '') || (ev.document_number != null && ev.document_number !== '') ? [ev.document_series, ev.document_number].filter(Boolean).join('-') : ev.document_code}>
                          {(ev.document_series != null && ev.document_series !== '') || (ev.document_number != null && ev.document_number !== '') ? [ev.document_series, ev.document_number].filter(Boolean).join('-') : ev.document_code}
                        </Link>
                      ) : ev.service_id && ev.service_sku ? (
                        <Link to={`/services/${ev.service_id}/edit`} className="text-teal-600 hover:text-teal-800 font-medium truncate block" title={ev.service_sku}>
                          {ev.service_sku}
                        </Link>
                      ) : ev.entry_guide_id && ev.entry_guide_code ? (
                        <Link to={`/entry-guides/${ev.entry_guide_id}/edit`} className="text-teal-600 hover:text-teal-800 font-medium truncate block" title={ev.entry_guide_code}>
                          {ev.entry_guide_code}
                        </Link>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-gray-900 tabular-nums">
                      {ev.hours != null ? formatBitacoraReading(ev.hours) : '—'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-gray-900 tabular-nums">
                      {ev.kilometers != null ? formatBitacoraReading(ev.kilometers) : '—'}
                    </td>
                    <td className="px-2 py-2 text-gray-500 min-w-0 align-top overflow-hidden">
                      <span className="block truncate text-[11px] sm:text-xs" title={ev.event_type || ''}>{ev.event_type || '—'}</span>
                    </td>
                    <td className="px-2 py-2 text-gray-500 min-w-0 align-top overflow-hidden">
                      <span className="block truncate text-[11px] sm:text-xs" title={ev.operator || ''}>{ev.operator || '—'}</span>
                    </td>
                    <td className="px-2 py-2 text-gray-600 min-w-0 align-top overflow-hidden">
                      <span className="block truncate text-[11px] sm:text-xs" title={ev.project_name || ''}>{ev.project_name || '—'}</span>
                    </td>
                    <td className="px-2 py-2 text-gray-500 min-w-0 align-top overflow-hidden">
                      <span className="block truncate text-[11px] sm:text-xs" title={ev.notes || ''}>{ev.notes || '—'}</span>
                    </td>
                    {isSingleResourceView && (
                      <td className="px-2 py-2 text-center align-top">
                        {(ev.shift_inconsistencies || []).length > 0 ? (
                          <button
                            type="button"
                            onClick={() => setShiftIssuesModal({ open: true, event: ev })}
                            className="inline-flex flex-col items-center gap-0.5 text-red-600 hover:text-red-800"
                            title="Ver inconsistencias de jornada"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <span className="text-[10px] font-semibold">{ev.shift_inconsistencies.length}</span>
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-2 py-2 text-right font-medium align-top whitespace-nowrap">
                      <div className="flex flex-col items-end gap-0.5 text-[11px] sm:text-xs">
                        {ev.document_id ? (
                          <Link to={resolveDocumentRoute(ev)} className="text-teal-600 hover:text-teal-900">
                            {ev.document_item_is_direct_consumption ? 'Consumo' : 'Doc.'}
                          </Link>
                        ) : ev.service_id ? (
                          <Link to={`/services/${ev.service_id}/edit`} className="text-teal-600 hover:text-teal-900">Serv.</Link>
                        ) : ev.entry_guide_id ? (
                          <Link to={`/entry-guides/${ev.entry_guide_id}/edit`} className="text-teal-600 hover:text-teal-900">Guía</Link>
                        ) : (
                          <>
                            <Link to={`/machinery/events/${ev.id}/edit`} className="text-teal-600 hover:text-teal-900">Editar</Link>
                            <button type="button" onClick={() => handleDelete(ev)} className="text-red-600 hover:text-red-900">Eliminar</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {shiftIssuesModal.open && shiftIssuesModal.event && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Inconsistencias de jornada</h2>
              <p className="mt-1 text-sm text-gray-600">
                {shiftIssuesModal.event.event_type} — {shiftIssuesModal.event.event_date}
                {shiftIssuesModal.event.event_time ? ` ${normalizeTime(shiftIssuesModal.event.event_time)}` : ''}
              </p>
            </div>
            <ul className="max-h-80 overflow-y-auto px-5 py-4 space-y-2 text-sm text-gray-800 list-disc list-inside">
              {(shiftIssuesModal.event.shift_inconsistencies || []).map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
            <div className="flex justify-end border-t border-gray-200 px-5 py-3">
              <button
                type="button"
                onClick={() => setShiftIssuesModal({ open: false, event: null })}
                className="px-4 py-2 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-700"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setEventToDelete(null); }}
        onConfirm={confirmDelete}
        title="Eliminar Evento"
        message="¿Estás seguro de eliminar este evento?"
      />
      <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, isOpen: false })} />
    </div>
  );
};

export default Events;
